import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import QRCode from 'qrcode';
import { Shortlink } from '../models/shared/Shortlink';
import { ColorInput, SegmentedToggle, Text } from '@wix/design-system';
import { dashboard } from "@wix/dashboard";
import { createClient } from "@wix/sdk";

let client: null | ReturnType<typeof createClient> = null;

interface QRCodeModalProps {
  onHide: () => void;
  shortlink: Shortlink;
  shortUrl: string;
}

const isValidHexColor = (color: string) => color.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/);

const QR_DISPLAY_SIZE = 300;
const QR_DOWNLOAD_SIZE = 1200;
const LOGO_RATIO = 0.22;

const renderQRToCanvas = (canvas: HTMLCanvasElement, url: string, size: number, color: string, logoUrl: string): Promise<void> => {
  return QRCode.toCanvas(canvas, url, {
    errorCorrectionLevel: 'H',
    width: size,
    color: { dark: color, light: '#FFF' }
  }).then(() => {
    if (!logoUrl) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    return new Promise<void>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const logoSize = size * LOGO_RATIO;
        const x = (size - logoSize) / 2;
        const y = (size - logoSize) / 2;
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(x - 4, y - 4, logoSize + 8, logoSize + 8);
        ctx.drawImage(img, x, y, logoSize, logoSize);
        resolve();
      };
      img.src = logoUrl;
    });
  });
};

const QRCodeModal: React.FC<QRCodeModalProps> = (props) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [color, setColor] = useState('#000');
  const [format, setFormat] = useState<'svg' | 'png'>('png');
  const [logoUrl, setLogoUrl] = useState('');
  // const [logoUrl, setLogoUrl] = useState('https://static.wixstatic.com/media/896d2a_b372282cdc2c4091920504b25f4035ee~mv2.png');

  useEffect(() => {
    if (!canvasRef.current || !isValidHexColor(color)) {
      return;
    }

    renderQRToCanvas(canvasRef.current, props.shortUrl, QR_DISPLAY_SIZE, color, logoUrl);
  }, [props.shortUrl, color, logoUrl])

  const handleChooseLogo = async () => {
    if (!client) {
      client = createClient({
        host: dashboard.host(),
        auth: dashboard.auth(),
        modules: { dashboard },
      });
    }

    const result = await client.dashboard.openMediaManager({ category: 'IMAGE' });
    if (result?.items?.length) {
      setLogoUrl(result.items[0].url || '');
      setFormat('png');
    }
  };

  const downloadQR = async () => {
    if (!canvasRef.current) {
      return;
    }

    try {

      let objectURL;
      if (format === 'svg') {
        const svgString = await QRCode.toString(props.shortUrl, {
          errorCorrectionLevel: 'H',
          type: 'svg',
          color: {
            dark: color,
            light: '#FFF'
          }
        });

        const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
        objectURL = URL.createObjectURL(svgBlob);
      } else {
        const offscreen = document.createElement('canvas');
        await renderQRToCanvas(offscreen, props.shortUrl, QR_DOWNLOAD_SIZE, color, logoUrl);
        objectURL = offscreen.toDataURL("image/png").replace("image/png", "image/octet-stream");
      }

      const downloadLink = document.createElement("a");
      downloadLink.href = objectURL;
      downloadLink.download = `${props.shortlink.name}.${format}`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      URL.revokeObjectURL(objectURL);

    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Modal show={true} onHide={props.onHide} centered >
      <Modal.Header closeButton>
        <Modal.Title>
          {props.shortlink.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body >
        <h4>Customize your QR code and download</h4>
        <label>Customize</label>
        <div className='wrapper d-flex'>
          <div className='flex-1 d-flex flex-column' style={{ gap: '12px' }}>
            <div className='center'>
              <canvas className='m-1' ref={canvasRef} />
            </div>

            <div>
              <Text>Primary Color</Text>
              <ColorInput value={color} onChange={color => setColor(color as string)} />
            </div>

            <div>
              <Text>Logo</Text>
              <div className='d-flex align-items-center' style={{ gap: '8px' }}>
                <Button variant='outline-primary' size='sm' onClick={handleChooseLogo}>
                  {logoUrl ? 'Change Logo' : 'Choose Logo'}
                </Button>
                {logoUrl && (
                  <Button variant='outline-danger' size='sm' onClick={() => setLogoUrl('')}>
                    Remove
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Text>File Format</Text>
              <SegmentedToggle selected={format} onClick={(_, value) => setFormat(value as any)}>
                <SegmentedToggle.Button value="png">PNG</SegmentedToggle.Button>
                <SegmentedToggle.Button value="svg">SVG</SegmentedToggle.Button>
              </SegmentedToggle>
              {format === 'svg' && logoUrl && <Text size="small" skin='error'>Logo will not be included in SVG format</Text>}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className='d-flex justify-content-end'>
          <Button variant='outline-primary' onClick={props.onHide} className='me-2'>
            Cancel
          </Button>
          <Button variant="primary" onClick={downloadQR}>
            Download
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export { QRCodeModal };